import { useLayoutEffect, useState } from 'react';
import './form-page.css';

/** Keep a creation page's existing action bar above the mobile keyboard. */
export function useFormPageViewport() {
  const [page, pageRef] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    if (!page) return;
    const viewport = window.visualViewport;
    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      const mobile = matchMedia('(max-width:1023px)').matches;
      const keyboard = Boolean(mobile && viewport && innerHeight - viewport.height > 80);
      page.dataset.keyboardOpen = String(keyboard);
      page.style.setProperty(
        '--form-keyboard-bottom',
        `${keyboard ? Math.max(0, innerHeight - viewport!.height - viewport!.offsetTop) : 0}px`,
      );
      frame = requestAnimationFrame(() => {
        const field = document.activeElement;
        if (
          !keyboard ||
          !(field instanceof HTMLElement) ||
          !page.contains(field) ||
          !field.matches('input,textarea,select,[contenteditable="true"]')
        )
          return;
        const footer = page.querySelector('[data-form-footer]')?.getBoundingClientRect();
        const rect = field.getBoundingClientRect();
        const top = (viewport?.offsetTop ?? 0) + 12;
        const bottom = (footer?.top ?? viewport!.height + viewport!.offsetTop) - 12;
        if (rect.height > bottom - top) return; // Native textarea caret scrolling handles tall notes.
        if (rect.bottom > bottom) window.scrollBy(0, rect.bottom - bottom);
        else if (rect.top < top) window.scrollBy(0, rect.top - top);
      });
    };
    update();
    page.addEventListener('focusin', update);
    viewport?.addEventListener('resize', update);
    viewport?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      cancelAnimationFrame(frame);
      page.removeEventListener('focusin', update);
      viewport?.removeEventListener('resize', update);
      viewport?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      page.style.removeProperty('--form-keyboard-bottom');
      delete page.dataset.keyboardOpen;
    };
  }, [page]);
  return { pageRef };
}
